import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import { useAuth } from './auth';
import { Button, Card, ErrorAlert, TextArea } from './ui';

type Kind = 'projects' | 'structures';

interface AgentNoteDto {
  id: number;
  note: string;
  userId: number;
  userName: string | null;
  createdAt: string | null;
}

interface QuoteCompletedResponse {
  quoteCompleted: string | null;
}

/**
 * Admin-only sidebar card surfacing CRM features (agent notes log + a
 * quote-completed toggle) for either a project folder or a structure.
 * Mirrors the legacy SiteMetrics / agent-note workflow. Renders nothing
 * for non-admin users.
 */
export function AdminAgentNotesCard({
  kind,
  id,
  initialQuoteCompleted,
}: {
  kind: Kind;
  id: number;
  initialQuoteCompleted: string | null;
}) {
  const { user } = useAuth();
  const isAdmin = !!user?.roles.some((r) => r === 'admin' || r === 'super-admin');
  const qc = useQueryClient();
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [quoteCompleted, setQuoteCompleted] = useState<string | null>(initialQuoteCompleted);

  const list = useQuery({
    queryKey: ['agent-notes', kind, id],
    enabled: isAdmin,
    queryFn: () => api.get<AgentNoteDto[]>(`/api/v1/admin/${kind}/${id}/agent-notes`),
  });

  const add = useMutation({
    mutationFn: () => api.post<AgentNoteDto>(`/api/v1/admin/${kind}/${id}/agent-notes`, { note: draft.trim() }),
    onSuccess: () => {
      setDraft('');
      setError(null);
      qc.invalidateQueries({ queryKey: ['agent-notes', kind, id] });
    },
    onError: () => setError('Failed to add note.'),
  });

  const toggleQuote = useMutation({
    mutationFn: (completed: boolean) =>
      api.post<QuoteCompletedResponse>(`/api/v1/admin/${kind}/${id}/quote-completed`, { completed }),
    onSuccess: (res) => setQuoteCompleted(res.quoteCompleted),
    onError: () => setError('Failed to update quote-completed.'),
  });

  if (!isAdmin) return null;

  return (
    <Card
      title={`Agent CRM (${kind === 'projects' ? 'project' : 'structure'})`}
      actions={
        <label className="flex items-center gap-2 text-xs text-slate-700">
          <input
            type="checkbox"
            checked={quoteCompleted !== null}
            disabled={toggleQuote.isPending}
            onChange={(e) => toggleQuote.mutate(e.target.checked)}
          />
          Quote completed{quoteCompleted ? ` · ${quoteCompleted}` : ''}
        </label>
      }
    >
      <ErrorAlert message={error} />

      <div className="space-y-2">
        <TextArea
          rows={2}
          placeholder="Add an internal CRM note (admin only)…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
        <div className="flex justify-end">
          <Button disabled={add.isPending || !draft.trim()} onClick={() => add.mutate()}>
            {add.isPending ? 'Adding…' : 'Add note'}
          </Button>
        </div>
      </div>

      <div className="mt-4 space-y-2">
        {list.isLoading && <p className="text-xs text-slate-500">Loading notes…</p>}
        {list.data && list.data.length === 0 && (
          <p className="text-xs text-slate-500">No agent notes yet.</p>
        )}
        {list.data?.map((n) => (
          <div key={n.id} className="rounded-md border border-slate-200 bg-slate-50 p-2 text-xs">
            <div className="text-slate-500">
              {n.userName ?? `user #${n.userId}`}
              {n.createdAt ? ` · ${new Date(n.createdAt).toLocaleString()}` : ''}
            </div>
            <div className="mt-1 whitespace-pre-wrap text-slate-800">{n.note}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}
